"use client";
import { useSubscription } from "@/helper/useSubscription";
import useDeviceDetect from "@/hooks/useMobile";
import { setErrorMess } from "@/lib/features/statusSlice";
import { REPORT_TYPE } from "@/models/report";
import { OPTIONS_SUBSCRIPTION } from "@/models/subscription";
import { User } from "@/models/user";
import ReportServices from "@/services/main/reportServices";
import { setCountBookmark } from "@lib/features/reportSlice";
import { useAppDispatch, useAppSelector } from "@lib/hooks";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import BookmarkRemoveIcon from "@mui/icons-material/BookmarkRemove";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import ShareIcon from "@mui/icons-material/Share";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { Montserrat } from "next/font/google";
import Image from "next/image";
import React, {
  Dispatch,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import ReviewNotice from "../reviewNotice/ReviewNotice";
import ReviewNoticeShare from "../reviewNotice/ReviewNoticeShare";
import { SidebarState } from "./ReportContent";

interface ReportRightProps {
  // headerHeight?: number;
  reportContainerRef?: React.RefObject<HTMLDivElement | null>;
  setSidebarState: Dispatch<React.SetStateAction<SidebarState>>;
  pages: Array<{ id: number; title: string; thumbnailUrl?: string | null }>;
  isFullscreen?: boolean;
  onExitFullscreen?: () => void;
  onPageSelect: (pageId: number) => void;
  onVisiblePageChange?: (pageId: number) => void;
  // bookmarkReviewVisible?: boolean;
  isLogin?: boolean;
  reportType?: number;
  isPaymentEd?: boolean;
  reportId: number;
  isShare?: boolean;
  slug?: string;
  currentPage?: number;
  onShare?: (page: number | null, pages?: number[] | null) => void;
  pageRefs: React.RefObject<Array<HTMLDivElement | null>>;
  dataDetail?: any;
  user?: User | null;
  locale: string;
  isJumpingRef: React.RefObject<boolean>;
}

interface CreateBookmark {
  reportId: number;
  page: number;
  image: string;
  language: string;
}

const montserrat = Montserrat({
  subsets: ["latin"],
});

const ReportRight = forwardRef(function ReportRight(
  {
    // headerHeight = 0,
    isJumpingRef,
    dataDetail,
    pages,
    isFullscreen,
    onExitFullscreen,
    onPageSelect,
    onVisiblePageChange,
    isLogin = false,
    reportType = REPORT_TYPE.FREE,
    isPaymentEd = false,
    isShare,
    slug,
    onShare,
    reportId,
    currentPage,
    pageRefs,
    user,
    locale,
  }: ReportRightProps,
  ref
) {
  const t = useTranslations("messages");
  const { isLoadingPdf } = useAppSelector((state) => state.status);
  const queryClient = useQueryClient();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const isMobile = useDeviceDetect();
  const { allowShareReport } = useAppSelector((state) => state.report);
  const [openDialogWarning, setOpenDialogWarning] = useState<boolean>(false);
  const transformWrapperRef = useRef<any>(null);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [scale, setScale] = useState(1);
  const [imgLoaded, setImgLoaded] = useState<Record<number, boolean>>({});
  const isAtBottom = useRef(false);
  const [openMenuPageId, setOpenMenuPageId] = useState<number | null>(null);
  const toggleMenuForPage = (pageId: number | null) => {
    setOpenMenuPageId((prevId) => (prevId === pageId ? null : pageId));
    setCurrentIconId(pageId);
  };
  const [currentIconId, setCurrentIconId] = useState<number | null>(null);
  const [showMoreIcon, setShowMoreIcon] = useState(false);
  const hideIconTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const ignoreObserverUntil = useRef<number>(0);
  const jumpCurrentPageRef = useRef<number | null>(null);
  const isProgrammaticScroll = useRef(false);
  useImperativeHandle(ref, () => ({
    zoomIn: () => handleZoomIn(),
    zoomOut: () => handleZoomOut(),
    resetZoom: () => {
      if (transformWrapperRef.current) {
        transformWrapperRef.current.resetTransform();
      }
    },
  }));
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const { hasPermission } = useSubscription(user?.license);
  const handleZoomIn = useCallback(() => {
    if (transformWrapperRef.current) {
      const instance = transformWrapperRef.current;
      const step = 0.2;
      instance.zoomIn(step);
    }
  }, []);
  const handleZoomOut = useCallback(() => {
    if (transformWrapperRef.current) {
      const instance = transformWrapperRef.current;
      const step = 0.2;
      instance.zoomOut(step);
    }
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer || !currentPage) return;
    if (!isJumpingRef.current) return;
    // disconnect observer to avoid mid-scroll observer updates
    if (observerRef.current) {
      try {
        observerRef.current.disconnect();
      } catch (e) {}
    }

    const pageIndex = pages.findIndex((p) => p.id === currentPage);
    const pageElement = pageRefs.current[pageIndex];
    jumpCurrentPageRef.current = currentPage;
    if (!pageElement) {
      // element not available, reset flags and re-attach observer
      isProgrammaticScroll.current = false;
      isJumpingRef.current = false;
      determineVisiblePage();
      return;
    }

    // compute target using bounding rect (works with transform/scale)
    const computeTarget = () => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elRect = pageElement.getBoundingClientRect();
      const elCenterInContainer =
        elRect.top - containerRect.top + elRect.height / 2;
      const containerHeight = scrollContainer.clientHeight;
      const desired = Math.round(
        scrollContainer.scrollTop + elCenterInContainer - containerHeight / 2
      );
      const max = Math.max(0, scrollContainer.scrollHeight - containerHeight);
      return Math.max(0, Math.min(desired, max));
    };

    let finished = false;
    let retries = 0;
    const maxRetries = 6;

    isProgrammaticScroll.current = true;
    const initialTarget = computeTarget();
    try {
      scrollContainer.scrollTo({ top: initialTarget, behavior: "auto" });
    } catch (e) {
      scrollContainer.scrollTop = initialTarget;
    }

    // watch for layout shifts (images) and re-center if necessary
    const ro = new ResizeObserver(() => {
      if (finished) return;
      retries += 1;
      const newTarget = computeTarget();
      const curr = Math.round(scrollContainer.scrollTop);
      if (Math.abs(newTarget - curr) > 6 && retries <= maxRetries) {
        try {
          scrollContainer.scrollTo({ top: newTarget, behavior: "auto" });
        } catch (e) {
          scrollContainer.scrollTop = newTarget;
        }
      }
      if (retries > maxRetries) finish();
    });
    ro.observe(pageElement);

    const onScroll = () => {
      const curr = Math.round(scrollContainer.scrollTop);
      if (Math.abs(curr - initialTarget) <= 4) finish();
    };
    scrollContainer.addEventListener("scroll", onScroll, { passive: true });

    const fallbackMs = 500 + Math.min(pages.length * 10, 3000);
    const fallbackTimer = window.setTimeout(() => finish(), fallbackMs);

    function finish() {
      if (finished) return;
      finished = true;
      ro.disconnect();
      scrollContainer?.removeEventListener("scroll", onScroll);
      clearTimeout(fallbackTimer);
      isProgrammaticScroll.current = false;
      isJumpingRef.current = false;
      ignoreObserverUntil.current = Date.now() + 250; // 250ms buffer
      // ensure parent state stays on the intended page
      if (onVisiblePageChange && typeof currentPage === "number") {
        onVisiblePageChange(currentPage);
      }
      // re-attach observer
      determineVisiblePage();
    }

    // immediate check
    onScroll();

    return () => {
      ro.disconnect();
      scrollContainer.removeEventListener("scroll", onScroll);
      clearTimeout(fallbackTimer);
      isProgrammaticScroll.current = false;
    };
  }, [pages, isJumpingRef, pageRefs.current, currentPage]);

  const { data: dataBookmarks } = useQuery({
    queryKey: ["bookmarks", reportId, locale],
    queryFn: async () => {
      const result = await ReportServices.getReportBookmarks({
        reportId: reportId,
        language: locale,
      });
      dispatch(
        setCountBookmark({
          [reportId]: result?.data?.length || 0,
        })
      );
      return result?.data;
    },
    refetchOnWindowFocus: false,
    enabled: isLogin,
  });

  const mutationAddBookmark = useMutation({
    mutationKey: ["add-report-bookmark"],
    mutationFn: async (data: FormData) => {
      return await ReportServices.addReportBookmark(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bookmarks", reportId, locale],
      });
    },
  });

  const handleAddBookmark = useCallback(
    (data: CreateBookmark) => {
      const formData = new FormData();
      formData.append("reportId", String(data.reportId));
      formData.append("page", String(data.page));
      formData.append("image", data.image);
      formData.append("language", data.language);

      mutationAddBookmark.mutate(formData);
    },
    [mutationAddBookmark]
  );

  const observerRef = useRef<IntersectionObserver | null>(null);

  const determineVisiblePage = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !onVisiblePageChange || !pageRefs.current.length)
      return;
    if (
      isProgrammaticScroll.current ||
      isJumpingRef.current ||
      (ignoreObserverUntil.current && Date.now() < ignoreObserverUntil.current)
    ) {
      return;
    }
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Tạo observer mới
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current || isJumpingRef.current) {
          return;
        }

        const { scrollTop, clientHeight, scrollHeight } = scrollContainer;
        const scrollProgress = scrollTop / (scrollHeight - clientHeight);

        if (scrollProgress >= 0.995) {
          const lastPageId = pages[pages.length - 1]?.id;
          if (lastPageId && lastPageId !== currentPage) {
            onVisiblePageChange(lastPageId);
          }
          isAtBottom.current = true;
          return;
        } else {
          isAtBottom.current = false;
        }
        if (isAtBottom.current) return;
        let closestPageId: number | null = null;
        let closestDist = Infinity;

        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          const pageId = parseInt(el.dataset.pageId || "0", 10);
          if (!el || !pageId) return;
          // tính theo vị trí hiển thị thực tế (works with transform/scale/layout shift)
          const containerRect = scrollContainer.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          const elCenterInContainer =
            elRect.top - containerRect.top + elRect.height / 2;
          const containerCenter = clientHeight / 2;
          const dist = Math.abs(elCenterInContainer - containerCenter);
          if (dist < closestDist) {
            closestDist = dist;
            closestPageId = pageId;
          }
        });
        if (scrollTop <= 0) {
          closestPageId = pages[0]?.id || null;
        }
        if (closestPageId && closestPageId !== currentPage) {
          onVisiblePageChange(closestPageId);
        }
      },
      {
        root: scrollContainer,
        rootMargin: "0px",
        threshold: [0, 0.1, 0.3, 0.5, 0.7, 0.9, 1.0],
      }
    );

    // Observe tất cả các trang
    pageRefs.current.forEach((pageRef) => {
      if (pageRef && observerRef.current) {
        observerRef.current.observe(pageRef);
      }
    });
  }, [onVisiblePageChange, pageRefs, isJumpingRef, currentPage, pages]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
    const handleScroll = () => {
      if (!isProgrammaticScroll.current) {
        determineVisiblePage();
      }
    };
    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [determineVisiblePage]);

  useEffect(() => {
    const handleContextMenu = (event: Event) => {
      event.preventDefault();
    };

    const images = scrollContainerRef.current?.querySelectorAll("img");

    images?.forEach((img) => {
      img.addEventListener("contextmenu", handleContextMenu);
      img.addEventListener("dragstart", handleContextMenu);
      img.style.userSelect = "none";
      img.style.pointerEvents = "auto";
    });

    return () => {
      images?.forEach((img) => {
        img.removeEventListener("contextmenu", handleContextMenu);
        img.removeEventListener("dragstart", handleContextMenu);
      });
    };
  }, [pages]);

  const shouldShowReviewNotice = useCallback(() => {
    // if (reportType === REPORT_TYPE.FREE) {
    //   return !isLogin;
    // }
    if (
      reportType === REPORT_TYPE.PREMIUM ||
      reportType === REPORT_TYPE.STANDARD
    ) {
      return !(
        (isLogin && isPaymentEd) ||
        (isLogin && hasPermission(OPTIONS_SUBSCRIPTION.PREMIUM))
      );
    }
    return false;
  }, [hasPermission, isLogin, isPaymentEd, reportType]);

  const shouldUseImagePreviews = useCallback(() => {
    if (reportType === REPORT_TYPE.FREE) {
      return isLogin;
    }
    if (
      reportType === REPORT_TYPE.PREMIUM ||
      reportType === REPORT_TYPE.STANDARD
    ) {
      return isLogin && isPaymentEd;
    }
    return false;
  }, [isLogin, isPaymentEd, reportType]);

  const handleCheckAlReadyAddBookmark = useCallback(
    (page: number) => {
      return dataBookmarks?.some((item: any) => item.page === page);
    },
    [dataBookmarks]
  );

  const mutationCheckLimitShare = useMutation({
    mutationKey: ["check-limit-share"],
    mutationFn: async (data: {
      reportId: number | string;
      page: number | null;
      pages: number[] | null;
    }) => {
      const check = await ReportServices.checkLimitShare(data.reportId);
      return { ...check, page: data.page, pages: data.pages };
    },
    onSuccess: (res: any) => {
      if (res?.data?.accept) {
        if (res?.page) {
          onShare?.(res?.page, null);
        }
        if (res?.pages && res?.pages.length > 0) {
          onShare?.(null, res?.pages);
        }
      } else {
        handleClickOpenWarnning();
        return;
      }
    },
    onError: (res: any) => {
      dispatch(setErrorMess(t("common_something_went_wrong")));
    },
  });

  const handleMutationCheckLimitShare = (
    reportId: number | string,
    page: number | null,
    pages: number[] | null
  ) => {
    mutationCheckLimitShare.mutate({
      reportId: reportId,
      page: page,
      pages: pages,
    });
  };

  const handleClickOpenWarnning = () => {
    setOpenDialogWarning(true);
  };

  const handleCloseWarning = () => {
    setOpenDialogWarning(false);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const resizeObserver = new ResizeObserver(() => {
      if (transformWrapperRef.current) {
        transformWrapperRef.current.resetTransform();
      }
    });
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  const hasBookmarkAction = true; // Hiện tại, nút bookmark luôn có sẵn khi đã login
  const hasShareAction =
    allowShareReport || hasPermission(OPTIONS_SUBSCRIPTION.PREMIUM);
  const shouldShowMobileMenu = hasBookmarkAction || hasShareAction;

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    // Hàm để hiển thị icon và đặt lịch ẩn
    const triggerShowIcon = () => {
      // Dọn dẹp timeout cũ nếu có
      if (hideIconTimeoutRef.current) {
        clearTimeout(hideIconTimeoutRef.current);
      }
      // Hiển thị icon
      setShowMoreIcon(true);
      // Đặt timeout mới để ẩn icon sau 2.5 giây
      hideIconTimeoutRef.current = setTimeout(() => {
        setShowMoreIcon(false);
        toggleMenuForPage(currentIconId);
      }, 2000);
    };

    // Nếu là mobile, thiết lập listener
    if (isMobile) {
      // Hiển thị lần đầu khi component mount
      triggerShowIcon();

      if (scrollContainer) {
        scrollContainer.addEventListener("touchstart", triggerShowIcon);
      }
    }

    // Hàm dọn dẹp
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("touchstart", triggerShowIcon);
      }
      if (hideIconTimeoutRef.current) {
        clearTimeout(hideIconTimeoutRef.current);
      }
    };
  }, [isMobile]);

  return (
    <Box
      className={`flex-1 ${isFullscreen ? "h-screen" : "h-auto md:h-[1300px]"} flex flex-col bg-[#F0F2F9] relative ${isMobile ? "overflow-y-auto" : ""}`}
    >
      <Box
        ref={scrollContainerRef}
        tabIndex={0}
        className={`thin-scrollbar
          ${
            !isMounted
              ? ""
              : !isFullscreen && (shouldShowReviewNotice() || isShare)
                ? !isMobile
                  ? "!pb-[250px] !sm:pb-[2px]"
                  : !isLogin
                    ? "!pb-[230px] !sm:pb-[2px]"
                    : "!pb-[200px] !sm:pb-[2px]"
                : ""
          }`}
        sx={{
          overflowY: "auto",
          height: isFullscreen ? "100%" : "100%",
          maxBlockSize: "100%",
          maxHeight: "100%",
        }}
      >
        {/* Content Container */}
        <Box
          className="!px-4 md:!px-[180px] thin-scrollbar scroll-container"
          sx={{
            width: "100%",
            minHeight: "100vh",
            display: "block",
            padding: 0,
            margin: 0,
            position: "relative",
            backfaceVisibility: "hidden",
            transform: `scale(${scale})`,
            transformOrigin: scale < 1 ? "center top" : "center center",
            transition: "transform 0.2s ease-out",
            // Thêm lại padding bottom logic
            // paddingBottom: !isMounted
            //   ? "0px"
            //   : !isFullscreen && (shouldShowReviewNotice() || isShare)
            //     ? { xs: "250px", sm: "2px" }
            //     : "0px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "",
            }}
          >
            <AnimatePresence>
              {isLoadingPdf ? (
                <Box className="flex justify-center items-center h-full mt-52">
                  <CircularProgress size={40} />
                </Box>
              ) : (
                pages.map((page, idx, array) => (
                  <Box
                    key={page.id}
                    data-page-id={page.id}
                    id={`page-${page.id}`}
                    ref={(el: HTMLDivElement | null) => {
                      pageRefs.current[idx] = el;
                    }}
                    className="page-content bg-gray-50 flex items-center mt-4 md:mt-[48px] justify-center relative group"
                    sx={{
                      marginBottom: idx === array.length - 1 ? "48px" : "0",
                      width: `100%`,
                      maxWidth: "100%",
                      position: "relative",
                      overflow: "hidden",
                    }}
                    onClick={() => !isFullscreen && onPageSelect(page.id)}
                  >
                    {!isShare && isLogin && (
                      <Box
                        className={`absolute !top-2 md:!top-4 !right-2 md:!right-4 z-10`}
                      >
                        {isMobile ? (
                          shouldShowMobileMenu && (
                            // GIAO DIỆN MỚI CHO MOBILE (XỔ DỌC, RIÊNG LẺ)
                            <Box sx={{ position: "relative" }}>
                              {/*  hiệu ứng*/}

                              {showMoreIcon && (
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.8 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  {/* Nút 3 chấm */}
                                  <IconButton
                                    aria-label="more options"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleMenuForPage(page.id);
                                    }}
                                    className="!bg-white h-[26px] w-[26px] cursor-pointer hover:bg-white"
                                    sx={{
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                      borderRadius: "50%",
                                    }}
                                  >
                                    <MoreVertIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </motion.div>
                              )}

                              {/* Menu xổ dọc */}
                              <AnimatePresence>
                                {openMenuPageId === page.id && (
                                  <motion.div
                                    initial={{
                                      opacity: 0,
                                      scale: 0.95,
                                      y: -10,
                                    }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                    transition={{ duration: 0.15 }}
                                    style={{
                                      position: "absolute",
                                      top: "calc(100% + 8px)",
                                      right: 0,
                                      zIndex: 10,
                                      transformOrigin: "top right",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "8px",
                                        background: "white",
                                        borderRadius: "50px",
                                        boxShadow:
                                          "0 4px 12px rgba(0,0,0,0.15)",
                                      }}
                                    >
                                      {/* Lựa chọn 1: Đánh dấu */}
                                      <Tooltip
                                        title={
                                          handleCheckAlReadyAddBookmark(page.id)
                                            ? t(
                                                "report_content_remove_bookmark"
                                              )
                                            : t("report_content_add_bookmark")
                                        }
                                        placement="left"
                                      >
                                        <IconButton
                                          className="!bg-white !p-[7px]"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddBookmark({
                                              reportId: reportId,
                                              page: page.id,
                                              image: page.thumbnailUrl || "",
                                              language: locale,
                                            });
                                          }}
                                        >
                                          {handleCheckAlReadyAddBookmark(
                                            page.id
                                          ) ? (
                                            <BookmarkRemoveIcon className="w-5 h-5 text-gray-80" />
                                          ) : (
                                            <BookmarkAddIcon className="w-5 h-5 text-gray-80" />
                                          )}
                                        </IconButton>
                                      </Tooltip>

                                      {/* Lựa chọn 2: Chia sẻ */}
                                      {(allowShareReport ||
                                        hasPermission(
                                          OPTIONS_SUBSCRIPTION.PREMIUM
                                        )) && (
                                        <Tooltip
                                          title={t("report_content_add_share")}
                                          placement="left"
                                        >
                                          <IconButton
                                            className="!bg-white !p-[7px]"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleMutationCheckLimitShare(
                                                reportId,
                                                page.id,
                                                null
                                              );
                                            }}
                                          >
                                            <ShareIcon className="w-5 h-5 text-gray-80" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </Box>
                          )
                        ) : (
                          <Box className="w-[40px] hidden group-hover:flex flex-col gap-[16px]">
                            <Tooltip
                              title={
                                <span
                                  translation-key={
                                    handleCheckAlReadyAddBookmark(page.id)
                                      ? "report_content_remove_bookmark"
                                      : "report_content_add_bookmark"
                                  }
                                  className={montserrat.className}
                                >
                                  {handleCheckAlReadyAddBookmark(page.id)
                                    ? t("report_content_remove_bookmark")
                                    : t("report_content_add_bookmark")}
                                </span>
                              }
                              disableInteractive
                              placement="bottom"
                              slotProps={{
                                popper: {
                                  modifiers: [
                                    {
                                      name: "offset",
                                      options: {
                                        offset: [0, -14],
                                      },
                                    },
                                  ],
                                },
                              }}
                            >
                              <IconButton
                                className="!bg-white !rounded h-[36px] md:h-[40px] cursor-pointer hover:bg-white bg-gray_2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddBookmark({
                                    reportId: reportId,
                                    page: page.id,
                                    image: page.thumbnailUrl || "",
                                    language: locale,
                                  });
                                }}
                              >
                                {handleCheckAlReadyAddBookmark(page.id) ? (
                                  <BookmarkRemoveIcon className="w-5 h-5 md:w-6 md:h-6 text-gray-80" />
                                ) : (
                                  <BookmarkAddIcon className="w-5 h-5 md:w-6 md:h-6 text-gray-80" />
                                )}
                              </IconButton>
                            </Tooltip>
                            {(allowShareReport ||
                              hasPermission(OPTIONS_SUBSCRIPTION.PREMIUM)) && (
                              <Tooltip
                                title={
                                  <span className={montserrat.className}>
                                    {t("report_content_add_share")}
                                  </span>
                                }
                                disableInteractive
                                placement="bottom"
                                slotProps={{
                                  popper: {
                                    modifiers: [
                                      {
                                        name: "offset",
                                        options: {
                                          offset: [0, -14],
                                        },
                                      },
                                    ],
                                  },
                                }}
                              >
                                <IconButton
                                  className="!bg-white !p-1 !rounded h-[36px] md:h-[40px] max-h-[36px] md:max-h-[40px] cursor-pointer hover:bg-white bg-gray_2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMutationCheckLimitShare(
                                      reportId,
                                      page.id,
                                      null
                                    );
                                  }}
                                >
                                  <ShareIcon className="w-5 h-5 md:w-6 md:h-6 text-gray-80" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                    {isShare && (
                      <Box
                        sx={{
                          height: "40px",
                          top: "8px",
                          right: "8px",
                          gap: "10px",
                          padding: "8px",
                          position: "absolute",
                          display: "flex",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                          zIndex: 10,
                        }}
                      >
                        <Box
                          sx={{
                            fontFamily: montserrat.style.fontFamily,
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0%",
                          }}
                          translation-key="report_content_shared_by"
                        >
                          {t("report_content_shared_by")}{" "}
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: "16px",
                              lineHeight: "24px",
                              letterSpacing: "0%",
                            }}
                          >
                            {dataDetail?.nameUserShared}
                          </span>
                        </Box>
                      </Box>
                    )}
                    <Image
                      src={
                        shouldUseImagePreviews()
                          ? page.thumbnailUrl ||
                            "/images/default-page-thumbnail.png"
                          : page.thumbnailUrl ||
                            "/images/default-page-thumbnail.png"
                      }
                      priority={[0, 1, 2, 3, 4].includes(idx)}
                      alt={page.title}
                      width={600}
                      height={800}
                      quality={90}
                      sizes="(max-width: 600px) 80vw, (max-width: 1200px) 60vw, 60vw"
                      decoding={"sync"}
                      loading={"eager"}
                      style={{
                        width: `100%`,
                        height: `100%`,
                        maxWidth: "100%",
                        objectFit: "contain",
                      }}
                      onLoad={() =>
                        setImgLoaded((prev) => ({ ...prev, [page.id]: true }))
                      }
                    />
                    {!imgLoaded[page.id] && (
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        animation="wave"
                        sx={{
                          bgcolor: "#eee",
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      />
                    )}
                  </Box>
                ))
              )}
            </AnimatePresence>
          </Box>
        </Box>
      </Box>
      {/* Zoom indicator */}

      {/* Zoom controls */}
      {/* {isFullscreen && (
        <Box className="!absolute !bottom-4 !left-4 md:!bottom-5 md:!left-5 !z-50 flex flex-col items-center gap-2">
          <Tooltip
            title={
              <span className={montserrat.className}>
                {t("report_content_zoom_info", {
                  defaultValue: "Ctrl + Scroll to zoom",
                })}
              </span>
            }
            placement="right"
          >
            <Box
              className="!bg-white !shadow-md !rounded-full !px-3 !py-1 !text-sm"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                fontWeight: "medium",
                opacity: 0.8,
              }}
            >
              {zoomPercentage}%
            </Box>
          </Tooltip>
          {!isMobile && (
            <Box className="flex gap-1">
              <Tooltip
                title={
                  <span className={montserrat.className}>
                    {t("report_header_default_tooltip")}
                  </span>
                }
                placement="top"
                disableInteractive
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: { offset: [0, -14] },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  className="!bg-white !shadow-md !rounded-full !w-8 !h-8"
                  onClick={() => handleReset()}
                  aria-label={t("report_header_default_tooltip")}
                >
                  <CenterFocusStrongSharpIcon className="!w-4 !h-4 text-gray-800" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  <span className={montserrat.className}>
                    {t("report_content_zoom_out", { defaultValue: "Zoom Out" })}
                  </span>
                }
                placement="right"
              >
                <IconButton
                  onClick={handleZoomOut}
                  className="!bg-white !shadow-md !rounded-full !w-8 !h-8"
                  size="small"
                >
                  <RemoveIcon className="!w-4 !h-4 text-gray-800" />
                </IconButton>
              </Tooltip>

              <Tooltip
                title={
                  <span className={montserrat.className}>
                    {t("report_content_zoom_in", { defaultValue: "Zoom In" })}
                  </span>
                }
                placement="right"
              >
                <IconButton
                  onClick={handleZoomIn}
                  className="!bg-white !shadow-md !rounded-full !w-8 !h-8"
                  size="small"
                >
                  <AddIcon className="!w-4 !h-4 text-gray-800" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      )} */}
      {/* {!isFullscreen && !isShare && shouldShowReviewNotice() && ( */}
      <ReviewNotice
        isFullscreen={isFullscreen}
        isShare={isShare}
        shouldShowReviewNotice={shouldShowReviewNotice}
        isLogin={isLogin}
        reportType={reportType}
        isPaymentEd={isPaymentEd}
        isMobile={isMobile}
        locale={locale}
      />
      {/* )} */}
      {/* {isShare && ( */}
      <ReviewNoticeShare
        isShare={isShare}
        slug={slug}
        isLogin={isLogin}
        reportType={reportType}
        isPaymentEd={isPaymentEd}
        isMobile={isMobile}
      />
      {/* )} */}

      {/* Exit fullscreen button */}
      {isFullscreen && (
        <Tooltip
          title={
            <span
              className={`${montserrat.className}`}
              translation-key="report_content_exit_fullscreen"
            >
              {t("report_content_exit_fullscreen")}
            </span>
          }
          placement="top"
        >
          <IconButton
            onClick={onExitFullscreen}
            className="!absolute !bottom-4 !right-4 md:!bottom-5 md:!right-5 !bg-white !shadow-md !rounded-full !w-10 !h-10 md:!w-12 md:!h-12 !z-50"
            aria-label={t("report_content_exit_fullscreen")}
          >
            <FullscreenExitIcon className="text-gray-800 w-5 h-5 md:w-6 md:h-6" />
          </IconButton>
        </Tooltip>
      )}

      {/* BookmarkReview */}
      {/* <AnimatePresence mode="wait">
        {!isFullscreen &&
          bookmarkReviewVisible &&
          (isMobile ? (
            <BookmarkReviewMobile
              sidebarState={sidebarState}
              handleCalculatePercentage={handleCalculatePercentage}
              handleMutationCheckLimitShare={handleMutationCheckLimitShare}
              onShare={onShare}
              reportId={reportId}
              isLoading={isLoading}
              data={dataBookmarks}
              pages={pages}
              selectedPage={selectedPage}
              onPageSelect={onPageSelect}
              isLogin={isLogin}
              reportType={reportType}
              isPaymentEd={isPaymentEd}
            />
          ) : (
            <BookmarkReview
              sidebarState={sidebarState}
              handleCalculatePercentage={handleCalculatePercentage}
              handleMutationCheckLimitShare={handleMutationCheckLimitShare}
              onShare={onShare}
              reportId={reportId}
              isLoading={isLoading}
              data={dataBookmarks}
              pages={pages}
              selectedPage={selectedPage}
              onPageSelect={onPageSelect}
              isLogin={isLogin}
              reportType={reportType}
              isPaymentEd={isPaymentEd}
            />
          ))}
      </AnimatePresence> */}

      <Dialog
        open={openDialogWarning}
        onClose={handleCloseWarning}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{}}
      >
        <DialogTitle
          id="alert-dialog-title"
          component={"div"}
          className="flex flex-col items-center gap-1 !p-6"
        >
          <Box className="w-full relative h-4">
            <IconButton
              className="!absolute !right-0 !top-1/2 !-translate-y-1/2 rounded-full !bg-cimigo-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                handleCloseWarning();
              }}
              size="small"
            >
              <CloseIcon className="!w-6 !h-6 !text-cimigo-gray-700" />
            </IconButton>
          </Box>
          <Box className="flex items-center justify-start gap-4 w-full">
            <Box className="!bg-[#FBF3DF] flex items-center justify-center p-2">
              <PriorityHighIcon className="!text-orange-dark" />
            </Box>
            <Box className="flex items-start justify-start">
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  fontSize: "18px",
                  lineHeight: "32px",
                  color: "var(--cimigo-gray-1100)",
                }}
                translation-key={"bookmark_share_litmit_pages_title_warning"}
              >
                {t("bookmark_share_litmit_pages_title_warning")}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent className="!p-6 !pb-8 !pt-0">
          <DialogContentText
            id="alert-dialog-description"
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "24px",
              color: "var(--cimigo-gray-1100)",
            }}
            translation-key={"bookmark_share_litmit_text_warning"}
          >
            {t("bookmark_share_litmit_text_warning")}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </Box>
  );
});

export default ReportRight;
